import classNames from "classnames";
import { Children, forwardRef, type HTMLAttributes, type ReactNode } from "react";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** @deprecated use density="compact" instead */
  densed?: boolean;
  density?: "comfortable" | "compact" | "dense";
  overflowVisible?: boolean;
  variant?: "glass" | "plain" | "surface";
  interactive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      title,
      description,
      actions,
      children,
      className,
      densed = false,
      density,
      overflowVisible = false,
      variant = "glass",
      interactive = false,
      ...props
    },
    ref
  ) => {
    const isGlass = variant === "glass";
    const isSurface = variant === "surface";
    const resolvedDensity = density || (densed ? "compact" : "comfortable");

    const densityClasses = {
      comfortable: "gap-4 p-5 md:p-6",
      compact: "gap-3 p-4 md:p-5",
      dense: "gap-2 p-3 md:p-4",
    };

    const isNotEmpty = (value: ReactNode) => {
      if (value == null || value === false) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0 && Children.toArray(value).length > 0;
      }

      return true;
    };

    return (
      <div
        ref={ref}
        className={classNames(
          "relative grid min-w-0 grid-cols-12 rounded-2xl border border-solid font-sans transition-all duration-200",
          overflowVisible ? "overflow-visible" : "overflow-hidden",
          densityClasses[resolvedDensity],
          isGlass
            ? [
              "border-white/20 dark:border-primary-800/70",
              "bg-white/20 dark:bg-gradient-to-b dark:from-content-900 dark:to-primary-950/70",
              "backdrop-blur-xl",
              "shadow-[0_8px_30px_-12px_rgba(15,23,42,0.15)]",
              interactive && "hover:border-white/30 hover:bg-white/30 dark:hover:border-primary-600/75 dark:hover:from-content-800 dark:hover:to-primary-900/80 hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.2)]",
            ]
            : isSurface
            ? [
              "border-transparent",
              "bg-primary-50/50 dark:bg-content-800/75",
              interactive && "hover:bg-primary-50 dark:hover:bg-content-800",
            ]
            : [
              "border-content-200 dark:border-content-700/70",
              "bg-white dark:bg-content-900",
              "shadow-sm",
              interactive && "hover:border-content-300 dark:hover:border-content-600",
            ],
          className
        )}
        {...props}
      >
        {isGlass && (
          <>
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(130%_90%_at_8%_0%,rgba(255,255,255,0.4)_0%,rgba(255,255,255,0.15)_45%,transparent_100%)] dark:hidden" />
          </>
        )}

        {/* Content */}
        <div className="relative col-span-12 min-w-0">
          {(title || actions) && (
            <div className="flex min-w-0 flex-col gap-3 border-b border-white/20 pb-3 text-base dark:border-primary-800/45 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="font-header text-lg font-medium text-content-900 dark:text-content-100">
                  {title}
                </div>
                {description && (
                  <div className="font-sans text-sm text-content-600 dark:text-content-300">
                    {description}
                  </div>
                )}
              </div>
              {actions && <div className="flex w-full min-w-0 flex-wrap items-center gap-2 lg:w-auto lg:flex-nowrap">{actions}</div>}
            </div>
          )}
          {isNotEmpty(children) &&
            <div className={classNames("grid min-w-0 grid-cols-12 gap-4", title || actions ? "mt-4 pt-1" : "")}>
              {children}
            </div>
          }
        </div>
      </div>
    );
  }
);

Card.displayName = "Card";
